import { doc, getDoc } from 'firebase/firestore';
import {
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { httpsCallable } from 'firebase/functions';

import { app, auth, db, firestorePathId, functions, isE2EEnvironment } from './firebase';
import {
  buildNotificationReconciliationKey,
  createNotificationReconciliationDeduper,
} from './notificationReconciliationInFlight.js';

const VAPID_KEY = 'BHKCnHRrlw2Ojo_F113jJqcgeC0H6J5WE33xG6c_VLJctl3plvNskkKJIYRxwOO7ccJ11oUMsXx9VwlA9OooXrE';
const LOCAL_REGISTRATION_KEY = 'sisfol_servidor_push_registration_v2';
const INSTALLATION_ID_KEY = 'sisfol_servidor_push_installation_id';
const LEGACY_TOKEN_KEY = 'sisfol_servidor_fcm_token';
const LEGACY_TOKEN_DOC_KEY = 'sisfol_servidor_fcm_token_doc';
const REGISTRATION_REFRESH_MS = 30 * 24 * 60 * 60 * 1000;

const getTokenCollectionPath = (uid) => (
  `artifacts/${firestorePathId}/users/${uid}/fcmTokens`
);

const safeJsonParse = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch (error) {
    return fallback;
  }
};

const readLocalRegistration = () => {
  try {
    return safeJsonParse(localStorage.getItem(LOCAL_REGISTRATION_KEY), null);
  } catch (error) {
    return null;
  }
};

const writeLocalRegistration = (registration) => {
  try {
    localStorage.setItem(LOCAL_REGISTRATION_KEY, JSON.stringify(registration));
  } catch (error) {
    // A ausência de storage persistente não deve impedir o push da sessão atual.
  }
};

const clearLegacyRegistration = () => {
  try {
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_DOC_KEY);
  } catch (error) {
    // Sem efeito funcional quando o navegador bloqueia storage.
  }
};

const clearLocalRegistration = () => {
  try {
    localStorage.removeItem(LOCAL_REGISTRATION_KEY);
  } catch (error) {
    // Sem efeito funcional quando o navegador bloqueia storage.
  }
  clearLegacyRegistration();
};

const getOrCreateInstallationId = () => {
  try {
    const current = String(localStorage.getItem(INSTALLATION_ID_KEY) || '').trim();
    if (/^[a-zA-Z0-9_-]{8,128}$/.test(current)) return current;

    const generated = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `web_${Date.now()}_${Math.random().toString(36).slice(2, 18)}`;
    localStorage.setItem(INSTALLATION_ID_KEY, generated);
    return generated;
  } catch (error) {
    return `session_${Date.now()}_${Math.random().toString(36).slice(2, 18)}`;
  }
};

const createTokenDocumentId = async (token) => {
  const bytes = new TextEncoder().encode(token);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('');
};

const timestampToMillis = (value) => {
  if (value?.toMillis) return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value || '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const emitRegistrationStatus = (detail) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('sisfol:push-registration-status', { detail }));
};

const normalizeMunicipioId = (value) => String(value || '').trim();
const notificationReconciliationDeduper = createNotificationReconciliationDeduper();


const postPushOwnerToServiceWorker = async (message) => {
  if (isE2EEnvironment || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false;
  try {
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration && message?.type === 'SISFOL_SET_PUSH_OWNER') {
      registration = await navigator.serviceWorker.ready;
    }
    const worker = registration?.active || navigator.serviceWorker.controller;
    if (!worker) return false;
    worker.postMessage(message);
    return true;
  } catch (error) {
    return false;
  }
};

export const setNotificationWorkerOwner = async ({ uid, municipioId }) => {
  const safeUid = String(uid || '').trim();
  const safeMunicipioId = normalizeMunicipioId(municipioId);
  if (!safeUid || !safeMunicipioId) return false;
  return postPushOwnerToServiceWorker({
    type: 'SISFOL_SET_PUSH_OWNER',
    uid: safeUid,
    municipioId: safeMunicipioId,
  });
};

export const clearNotificationWorkerOwner = async () => (
  postPushOwnerToServiceWorker({ type: 'SISFOL_CLEAR_PUSH_OWNER' })
);

const isTokenDocumentValidForContext = ({ data, token, uid, municipioId, installationId }) => (
  Boolean(data)
  && data.uid === uid
  && data.token === token
  && data.portal === 'servidor'
  && String(data.municipioId || '') === municipioId
  && String(data.installationId || '') === installationId
  && data.active !== false
);

const needsFreshnessRefresh = (data) => {
  const reference = data?.lastVerifiedAt || data?.updatedAt || data?.createdAt;
  const lastVerifiedAt = timestampToMillis(reference);
  return !lastVerifiedAt || Date.now() - lastVerifiedAt >= REGISTRATION_REFRESH_MS;
};

export const buildNotificationTag = (data = {}) => (
  [
    data.eventId,
    data.tipo,
    data.notificationId,
    data.messageId,
    data.avisoId,
    data.requerimentoId,
    data.roomId,
    data.status,
  ].filter(Boolean).map(String).join(':') || 'sisfol-servidor'
);

export const getNotificationSupport = async ({ municipioId } = {}) => {
  if (isE2EEnvironment) return { supported: false, permission: 'unsupported', registered: false };
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    return { supported: false, permission: 'unsupported', registered: false };
  }

  const supported = await isSupported().catch(() => false);
  const currentUser = auth.currentUser;
  const registration = readLocalRegistration();
  const expectedMunicipioId = normalizeMunicipioId(municipioId);
  const registered = Boolean(
    supported
    && Notification.permission === 'granted'
    && currentUser?.uid
    && registration?.uid === currentUser.uid
    && registration?.portal === 'servidor'
    && (!expectedMunicipioId || registration?.municipioId === expectedMunicipioId)
    && registration?.documentId
    && registration?.needsRotation !== true
  );

  return {
    supported,
    permission: supported ? Notification.permission : 'unsupported',
    registered,
  };
};

const reconcileCurrentNotificationRegistrationInternal = async ({
  municipioId,
  forceRefresh = false,
  reason = 'automatic',
} = {}) => {
  if (isE2EEnvironment) {
    return { supported: false, permission: 'unsupported', registered: false, skipped: true };
  }

  const currentUser = auth.currentUser;
  const expectedMunicipioId = normalizeMunicipioId(municipioId);
  const support = await getNotificationSupport({ municipioId: expectedMunicipioId });

  if (!support.supported) return support;
  if (Notification.permission !== 'granted') {
    const result = { ...support, registered: false };
    emitRegistrationStatus(result);
    return result;
  }
  if (!currentUser?.uid || !expectedMunicipioId) {
    const result = { ...support, registered: false, reason: 'missing-auth-context' };
    emitRegistrationStatus(result);
    return result;
  }

  const registration = await navigator.serviceWorker.ready;
  const messaging = getMessaging(app);
  const localRegistration = readLocalRegistration();
  const installationId = getOrCreateInstallationId();
  const contextChanged = Boolean(
    localRegistration?.uid
    && (
      localRegistration.uid !== currentUser.uid
      || localRegistration.portal !== 'servidor'
    )
  );

  if (contextChanged || localRegistration?.needsRotation === true) {
    await deleteToken(messaging).catch(() => false);
    clearLocalRegistration();
  }

  let token = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });

  if (!token) {
    const result = { ...support, registered: false, reason: 'missing-token' };
    emitRegistrationStatus(result);
    return result;
  }

  let tokenDocumentId = await createTokenDocumentId(token);
  let tokenRef = doc(db, getTokenCollectionPath(currentUser.uid), tokenDocumentId);
  let tokenSnapshot = await getDoc(tokenRef);

  // getToken() pode devolver a registration já armazenada localmente. Se esse
  // mesmo documento era conhecido por este navegador e desapareceu do backend,
  // não devemos simplesmente recriar o registro com a mesma registration: o
  // backend pode tê-la removido após o FCM classificá-la como inválida.
  const knownRegistrationWasRemoved = Boolean(
    localRegistration?.uid === currentUser.uid
    && localRegistration?.portal === 'servidor'
    && localRegistration?.documentId === tokenDocumentId
    && !tokenSnapshot.exists()
  );

  if (knownRegistrationWasRemoved) {
    const rotated = await deleteToken(messaging).catch(() => false);
    if (!rotated) {
      writeLocalRegistration({
        ...localRegistration,
        needsRotation: true,
        lastReconciledAt: Date.now(),
      });
      const result = {
        ...support,
        permission: 'granted',
        registered: false,
        reason: 'token-rotation-failed',
      };
      emitRegistrationStatus(result);
      return result;
    }

    token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) {
      const result = {...support, registered: false, reason: 'missing-token-after-rotation'};
      emitRegistrationStatus(result);
      return result;
    }
    tokenDocumentId = await createTokenDocumentId(token);
    tokenRef = doc(db, getTokenCollectionPath(currentUser.uid), tokenDocumentId);
    tokenSnapshot = await getDoc(tokenRef);
  }
  const tokenData = tokenSnapshot.exists() ? tokenSnapshot.data() : null;
  const validRegistration = tokenSnapshot.exists() && isTokenDocumentValidForContext({
    data: tokenData,
    token,
    uid: currentUser.uid,
    municipioId: expectedMunicipioId,
    installationId,
  });
  const refreshDue = validRegistration && needsFreshnessRefresh(tokenData);
  const mustRegister = forceRefresh || !validRegistration || refreshDue;

  if (mustRegister) {
    const registerDevice = httpsCallable(functions, 'registrarDispositivoPushServidor');
    try {
      await registerDevice({
        token,
        municipioId: expectedMunicipioId,
        installationId,
        userAgent: String(navigator.userAgent || '').slice(0, 500),
        platform: String(navigator.userAgentData?.platform || navigator.platform || 'nao_informado').slice(0, 120),
        reason: String(reason || 'automatic').slice(0, 60),
      });
    } catch (error) {
      if (!validRegistration) {
        const result = {
          ...support,
          permission: 'granted',
          registered: false,
          reason: 'backend-registration-failed',
          error,
        };
        emitRegistrationStatus(result);
        throw error;
      }
      // Um registro já válido continua utilizável mesmo se a renovação periódica falhar.
    }
  }

  const localState = {
    schemaVersion: 2,
    uid: currentUser.uid,
    municipioId: expectedMunicipioId,
    portal: 'servidor',
    documentId: tokenDocumentId,
    installationId,
    lastReconciledAt: Date.now(),
    needsRotation: false,
  };
  writeLocalRegistration(localState);
  clearLegacyRegistration();
  await setNotificationWorkerOwner({ uid: currentUser.uid, municipioId: expectedMunicipioId });

  const result = {
    supported: true,
    permission: 'granted',
    registered: true,
    repaired: !validRegistration,
    refreshed: refreshDue || forceRefresh,
    documentId: tokenDocumentId,
    installationId,
  };
  emitRegistrationStatus(result);
  return result;
};

export const reconcileCurrentNotificationRegistration = (options = {}) => {
  const {
    municipioId,
    forceRefresh = false,
  } = options;
  const expectedMunicipioId = normalizeMunicipioId(municipioId);
  const key = buildNotificationReconciliationKey({
    uid: auth.currentUser?.uid,
    municipioId: expectedMunicipioId,
    forceRefresh,
  });

  return notificationReconciliationDeduper.run(key, () => (
    reconcileCurrentNotificationRegistrationInternal({
      ...options,
      municipioId: expectedMunicipioId,
      forceRefresh,
    })
  ));
};

export const requestNotificationPermissionAndSaveToken = async ({ municipioId }) => {
  if (isE2EEnvironment) {
    throw new Error('Notificações push não são usadas no laboratório E2E local.');
  }
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) {
    throw new Error('Entre novamente no sistema para ativar as notificações.');
  }

  const support = await getNotificationSupport({ municipioId });
  if (!support.supported) {
    throw new Error('Este navegador não oferece suporte às notificações do SISFOL.');
  }

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();

  if (permission !== 'granted') {
    throw new Error('A permissão de notificação não foi concedida.');
  }

  return reconcileCurrentNotificationRegistration({
    municipioId,
    forceRefresh: true,
    reason: 'user-enable',
  });
};

export const revokeCurrentNotificationToken = async ({ reason = 'logout' } = {}) => {
  // Bloqueia a exibição local imediatamente, inclusive se o aparelho estiver
  // offline e a revogação remota/FCM não puder ser concluída naquele momento.
  await clearNotificationWorkerOwner();

  if (isE2EEnvironment) {
    clearLocalRegistration();
    return { revoked: true, skipped: true };
  }

  const currentUser = auth.currentUser;
  const localRegistration = readLocalRegistration();
  const installationId = String(localRegistration?.installationId || getOrCreateInstallationId());
  let backendRevoked = false;
  let messagingRevoked = false;

  if (
    currentUser?.uid
    && localRegistration?.uid === currentUser.uid
    && localRegistration?.documentId
  ) {
    try {
      const revokeDevice = httpsCallable(functions, 'revogarDispositivoPushServidor');
      await revokeDevice({
        tokenDocumentId: localRegistration.documentId,
        installationId,
        reason: String(reason || 'logout').slice(0, 60),
      });
      backendRevoked = true;
    } catch (error) {
      // A invalidação local do token ainda reduz o risco; o backend limpará token rejeitado no próximo envio.
    }
  }

  try {
    if (await isSupported().catch(() => false)) {
      messagingRevoked = await deleteToken(getMessaging(app));
    }
  } catch (error) {
    messagingRevoked = false;
  }

  if (messagingRevoked || backendRevoked) {
    clearLocalRegistration();
  } else if (localRegistration) {
    writeLocalRegistration({
      ...localRegistration,
      needsRotation: true,
      lastReconciledAt: Date.now(),
    });
    clearLegacyRegistration();
  } else {
    clearLegacyRegistration();
  }

  const result = { revoked: backendRevoked || messagingRevoked, backendRevoked, messagingRevoked };
  emitRegistrationStatus({ supported: true, permission: Notification.permission, registered: false, ...result });
  return result;
};

export const testCurrentNotificationDevice = async ({ municipioId }) => {
  const expectedMunicipioId = normalizeMunicipioId(municipioId);
  let registration = readLocalRegistration();
  const currentUser = auth.currentUser;

  if (
    !currentUser?.uid
    || registration?.uid !== currentUser.uid
    || registration?.municipioId !== expectedMunicipioId
    || !registration?.documentId
    || !registration?.installationId
  ) {
    await reconcileCurrentNotificationRegistration({
      municipioId: expectedMunicipioId,
      forceRefresh: true,
      reason: 'device-test-reconcile',
    });
    registration = readLocalRegistration();
  }

  if (!registration?.documentId || !registration?.installationId) {
    throw new Error('Este aparelho ainda não possui um registro de notificações válido.');
  }

  const testDevice = httpsCallable(functions, 'testarNotificacaoPushServidor');
  try {
    const response = await testDevice({
      municipioId: expectedMunicipioId,
      tokenDocumentId: registration.documentId,
      installationId: registration.installationId,
    });
    return response?.data || {success: true};
  } catch (error) {
    const errorText = String(error?.message || '').toLowerCase();
    if (errorText.includes('rejeitado pelo fcm') || errorText.includes('precisa ser reconciliado')) {
      try {
        if (await isSupported().catch(() => false)) await deleteToken(getMessaging(app));
      } catch (deleteError) {
        // A reconciliação abaixo continua sendo a fonte de recuperação.
      }
      clearLocalRegistration();
      await reconcileCurrentNotificationRegistration({
        municipioId: expectedMunicipioId,
        forceRefresh: true,
        reason: 'device-test-self-heal',
      }).catch(() => {});
    }
    throw error;
  }
};


export const listenForegroundMessages = async () => {
  if (isE2EEnvironment) return () => {};
  if (!await isSupported().catch(() => false)) return () => {};

  const messaging = getMessaging(app);
  return onMessage(messaging, async (payload) => {
    const data = payload.data || {};
    const currentUser = auth.currentUser;
    const localRegistration = readLocalRegistration();
    const recipientUid = String(data.recipientUid || data.userId || data.uid || '').trim();
    const payloadMunicipioId = normalizeMunicipioId(data.municipioId);
    const recipientScope = String(data.recipientScope || (recipientUid ? 'user' : '')).trim().toLowerCase();

    if (!currentUser?.uid || localRegistration?.uid !== currentUser.uid) return;
    if (payloadMunicipioId && localRegistration?.municipioId !== payloadMunicipioId) return;
    if (recipientScope !== 'municipality' && (!recipientUid || recipientUid !== currentUser.uid)) return;

    const registration = await navigator.serviceWorker.ready;
    const title = data.title || payload.notification?.title || 'Nova atualização no SISFOL';
    const options = {
      body: data.body || payload.notification?.body || 'Acesse o sistema para consultar.',
      icon: payload.notification?.icon || '/icons/icon-192x192.png',
      badge: '/icons/icon-192x192.png',
      tag: buildNotificationTag(data),
      renotify: false,
      data: {
        ...data,
        link: data.link || '/dashboard',
      },
    };

    await registration.showNotification(title, options);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sisfol:notification-received', {detail: data}));
    }
  });
};
